// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import {
  FileText,
  Warehouse,
  Layers,
  ArrowDownToLine,
  Droplet,
  Download,
  FileSpreadsheet,
  RotateCcw,
  Printer,
  SlidersHorizontal,
  Scissors,
  CheckCircle2,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatNumber } from '@/lib/utils';
import { PaperSlitConfirmModal } from './components/PaperSlitConfirmModal';
import { PalletJobTagPrintModal } from '@/components/print/PalletJobTagPrintModal';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { CompactKpiRibbon, type CompactKpiItem } from '@/components/shared/CompactKpiRibbon';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { ZIndexView, useZIndexView } from '@/components/shared/ZIndexView';
import { useRowSelection } from '@/hooks/useRowSelection';
import { DataTableRow, DataTableRowCheckbox, DataTableSelectAllCheckbox } from '@/components/shared/DataTableRow';
import { BulkActionBar } from '@/components/shared/BulkActionBar';
import { exportToExcel } from '@/lib/excel.utils';
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const rowKeyOf = (item: any, idx: number) => String(item.id || `${item.lsx}-${idx}`);

const EXPORT_COLUMNS = [
  { key: 'lsx', label: 'Mã LSX', width: 16 },
  { key: 'customerGroup', label: 'Khách hàng', width: 18 },
  { key: 'productName', label: 'Sản phẩm', width: 36, exportFn: (r: any) => r.productName || r.itemCode || '' },
  { key: 'poNumber', label: 'Đơn hàng', width: 16 },
  { key: 'layoutUps', label: 'Bát', width: 8, exportFn: (r: any) => r.layoutUps || 1 },
  { key: 'requiredSheets', label: 'Tờ Net (ĐM)', width: 12, exportFn: (r: any) => Math.round(Number(r.requiredSheets) || 0) },
  { key: 'issuedSheets', label: 'Kho cấp (tờ)', width: 12, exportFn: (r: any) => Math.round(Number(r.issuedSheets) || 0) },
  { key: 'bufferSheets', label: 'Bù hao (tờ)', width: 12, exportFn: (r: any) => Math.round(Number(r.bufferSheets) || 0) },
  { key: 'printedSheets', label: 'In chạy (tờ)', width: 12, exportFn: (r: any) => Math.round(Number(r.printedSheets) || 0) },
  { key: 'stockPcs', label: 'Giấy dư thu hồi (tờ)', width: 14, exportFn: (r: any) => Math.round(Number(r.stockPcs) || 0) },
  { key: 'isSlitted', label: 'Trạng thái phôi', width: 14, exportFn: (r: any) => (r.isSlitted ? 'Đã xả phôi' : 'Chờ xả phôi') },
  { key: 'palletNo', label: 'Pallet', width: 12 },
];

export function PaperAllocationAndSurplusHubPage({ hubTabs }: { hubTabs?: React.ReactNode } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const zIndex = useZIndexView();
  const [selectedGroup, setSelectedGroup] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'WAITING_SLIT' | 'SLITTED' | 'HAS_SURPLUS'>('ALL');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = usePageSizeState(50);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [returnSheets, setReturnSheets] = useState<number>(0);
  const [returnNotes, setReturnNotes] = useState<string>('');
  const [reclaimCategory, setReclaimCategory] = useState<'PAPER' | 'PANTONE_INK'>('PAPER');
  const [pantoneCode, setPantoneCode] = useState<string>('PANTONE 485C');
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);

  // In-flight Paper Buffer Modal states
  const [selectedBufferItem, setSelectedBufferItem] = useState<any | null>(null);
  const [bufferSheetsToAdd, setBufferSheetsToAdd] = useState<number>(100);
  const [bufferReason, setBufferReason] = useState<string>('Hao hụt căn chỉnh máy in');
  const [isBufferModalOpen, setIsBufferModalOpen] = useState(false);

  // Phase 40 Slitting & Pallet Modal states
  const [selectedSlitItem, setSelectedSlitItem] = useState<any | null>(null);
  const [isSlitModalOpen, setIsSlitModalOpen] = useState(false);
  const [selectedPrintItem, setSelectedPrintItem] = useState<any | null>(null);
  const [isPalletPrintModalOpen, setIsPalletPrintModalOpen] = useState(false);

  const { headerSearch } = useHeaderSearch();

  React.useEffect(() => {
    setPage(1);
  }, [headerSearch, selectedGroup, statusFilter]);

  // Fetch Paper Allocation & Surplus Data
  const { data, isLoading, refetch } = useQuery(
    trpc.productionPlanning.getPaperAllocationAndSurplus.queryOptions({
      customerGroup: selectedGroup !== 'ALL' ? selectedGroup : undefined,
      search: headerSearch || undefined,
      page,
      limit: pageSize,
    })
  );

  // Mutation to return surplus paper or Pantone spot ink to warehouse
  const returnMutation = useMutation(
    trpc.productionPlanning.returnPaperSurplusToWarehouse.mutationOptions({
      onSuccess: (res: any) => {
        toast.success(res.message || 'Thu hồi vật tư dư về kho NVL thành công!');
        setIsReturnModalOpen(false);
        queryClient.invalidateQueries({ queryKey: [['productionPlanning', 'getPaperAllocationAndSurplus']] });
        refetch();
      },
      onError: (err: any) => {
        toast.error(`Lỗi: ${err.message}`);
      }
    })
  );

  // Mutation to issue additional paper buffer in-flight
  const issueBufferMutation = useMutation(
    trpc.productionPlanning.issueAdditionalPaperBuffer.mutationOptions({
      onSuccess: (res: any) => {
        toast.success(res.message || 'Cấp bù giấy hao hụt thành công!');
        setIsBufferModalOpen(false);
        queryClient.invalidateQueries({ queryKey: [['productionPlanning', 'getPaperAllocationAndSurplus']] });
        refetch();
      },
      onError: (err: any) => {
        toast.error(`Lỗi cấp bù giấy: ${err.message}`);
      }
    })
  );

  const CUSTOMER_TABS = [
    { key: 'ALL', label: 'Tất cả nhóm KH' },
    { key: 'R-pac', label: 'R-PAC' },
    { key: 'Dream', label: 'Dream Plastics' },
    { key: 'JCV', label: 'JCV' },
    { key: 'Grand', label: 'Grand Champion' },
    { key: 'Basic', label: 'Basic' },
    { key: 'DANLI', label: 'DANLI' },
    { key: 'Thẻ', label: 'Thẻ Bài' },
  ];

  const handleOpenReturnModal = (item: any, category: 'PAPER' | 'PANTONE_INK' = 'PAPER') => {
    setSelectedItem(item);
    setReclaimCategory(category);
    if (category === 'PANTONE_INK') {
      setReturnSheets(2.5);
      setPantoneCode('PANTONE 485C');
      setReturnNotes(`Thu hồi mực pha dư Pantone của LSX ${item?.lsx || 'IN-OFFSET'}`);
    } else {
      setReturnSheets(Math.max(1, Math.round(Number(item?.stockPcs) || 50)));
      setReturnNotes(`Thu hồi giấy dư của LSX ${item?.lsx || ''}`);
    }
    setIsReturnModalOpen(true);
  };

  const handleOpenSlitModal = (item: any) => {
    setSelectedSlitItem(item);
    setIsSlitModalOpen(true);
  };

  const handleOpenPalletPrintModal = (item: any) => {
    setSelectedPrintItem(item);
    setIsPalletPrintModalOpen(true);
  };

  const handleConfirmReturn = () => {
    if (!selectedItem || returnSheets <= 0) {
      toast.error('Số lượng thu hồi phải lớn hơn 0');
      return;
    }
    if (reclaimCategory === 'PANTONE_INK' && !pantoneCode.trim()) {
      toast.error('Vui lòng nhập mã màu Pantone');
      return;
    }
    returnMutation.mutate({
      materialId: selectedItem.materialId || selectedItem.id,
      lsx: String(selectedItem.lsx || 'IN-OFFSET'),
      itemCode: String(selectedItem.itemCode || 'MAT'),
      returnSheets: Number(returnSheets),
      notes: returnNotes,
      materialCategory: reclaimCategory,
      pantoneCode: reclaimCategory === 'PANTONE_INK' ? pantoneCode.trim() : undefined,
      customerId: selectedItem.customerGroup || undefined,
    });
  };

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (statusFilter === 'ALL') return data.items;
    if (statusFilter === 'WAITING_SLIT') return data.items.filter((x: any) => !x.isSlitted);
    if (statusFilter === 'SLITTED') return data.items.filter((x: any) => x.isSlitted);
    if (statusFilter === 'HAS_SURPLUS') return data.items.filter((x: any) => (Number(x.stockPcs) || 0) > 0);
    return data.items;
  }, [data?.items, statusFilter]);

  const summary = data?.summary;

  const rowIds = useMemo(() => filteredItems.map((it: any, idx: number) => rowKeyOf(it, idx)), [filteredItems]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectedCount, clearSelection } = rowSelection;
  const exportRows = (rows: any[]) =>
    exportToExcel(rows, EXPORT_COLUMNS, 'cap_phat_giay', { sheetName: 'Cấp phát giấy' });

  const kpiItems = useMemo<CompactKpiItem[]>(() => {
    const items = data?.items || [];
    const waitingSlitCount = items.filter((x: any) => !x.isSlitted).length;
    const slittedCount = items.filter((x: any) => x.isSlitted).length;
    const surplusCount = items.filter((x: any) => (Number(x.stockPcs) || 0) > 0).length;

    return [
      {
        key: 'ALL',
        label: 'Tất cả lệnh cấp',
        count: formatNumber(summary?.totalRecords || items.length || 0),
        icon: FileText,
        variant: 'blue',
        tooltip: 'Hiển thị tất cả lượt cấp phát giấy',
      },
      {
        key: 'WAITING_SLIT',
        label: 'Chờ xả phôi',
        count: formatNumber(waitingSlitCount),
        icon: Scissors,
        variant: 'amber',
        tooltip: 'Lệnh chưa xả phôi',
      },
      {
        key: 'SLITTED',
        label: 'Đã xả phôi',
        count: formatNumber(slittedCount),
        icon: CheckCircle2,
        variant: 'indigo',
        tooltip: 'Lệnh đã xả phôi hoàn tất',
      },
      {
        key: 'HAS_SURPLUS',
        label: 'Giấy dư thu hồi',
        count: formatNumber(surplusCount),
        icon: ArrowDownToLine,
        variant: 'emerald',
        tooltip: `Có ${formatNumber(summary?.totalStockPcs || 0)} tờ giấy dư thu hồi`,
      },
    ];
  }, [summary, data?.items]);

  const customerTabsNode = (
    <div className="flex items-center gap-1 flex-nowrap shrink-0">
      {CUSTOMER_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => {
            setSelectedGroup(tab.key);
            setPage(1);
          }}
          className={`h-7 px-2.5 rounded text-xs font-medium transition-colors whitespace-nowrap shrink-0 cursor-pointer ${
            selectedGroup === tab.key
              ? 'bg-emerald-600 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const kpiRibbonNode = (
    <CompactKpiRibbon
      items={kpiItems}
      activeKey={statusFilter}
      onSelect={(key) => setStatusFilter((prev) => (prev === key ? 'ALL' : (key as any)))}
      isLoading={isLoading}
      className="w-auto"
    />
  );

  return (
    <>
      <ListPageShell
        onExport={() => exportRows(filteredItems)}
        unitLabel="lệnh cấp giấy"
        totalItems={data?.total || filteredItems.length}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
        }}
        isLoading={isLoading}
        tabs={hubTabs ?? customerTabsNode}
        headerFilters={
          hubTabs ? (
            <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
              {customerTabsNode}
              <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
              {kpiRibbonNode}
            </div>
          ) : (
            kpiRibbonNode
          )
        }
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              data-testid="btn-open-reclaim-pantone-ink"
              onClick={() =>
                handleOpenReturnModal(
                  filteredItems[0] || {
                    id: 'MAT-INK',
                    lsx: 'IN-OFFSET',
                    itemCode: 'INK-PANTONE',
                    customerGroup: 'Khách hàng',
                    stockPcs: 0,
                  },
                  'PANTONE_INK',
                )
              }
              className="gap-1 text-xs h-7 border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100 font-semibold"
              title="Thu hồi mực pha đặc chủng (Pantone Spot Ink) còn dư sau ca in về Thư viện Mực Pha Dư"
            >
              <Droplet className="h-3 w-3 text-purple-700" />
              Thu Hồi Mực
            </Button>
            <Button
              size="sm"
              onClick={() => navigate({ to: '/production-orders' })}
              className="gap-1 text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
            >
              <FileSpreadsheet className="h-3 w-3" />
              Lệnh Sản Xuất
            </Button>
          </div>
        }
      >
        <table className="w-full text-left text-xs border-collapse min-w-[1100px]">
          <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200 text-slate-700 font-semibold">
            <tr>
              <DataTableSelectAllCheckbox rowSelection={rowSelection} />
              <th className="py-2.5 px-3 w-24">Mã LSX</th>
              <th className="py-2.5 px-3 w-28">Khách Hàng</th>
              <th className="py-2.5 px-3 min-w-[180px]">Sản Phẩm & Đơn Hàng</th>
              <th className="py-2.5 px-3 w-16 text-center">Bát</th>
              <th className="py-2.5 px-3 text-right w-24">Tờ Net (ĐM)</th>
              <th className="py-2.5 px-3 text-right w-24 text-blue-700 bg-blue-50/50">Kho Cấp (Tờ)</th>
              <th className="py-2.5 px-3 text-right w-24 text-amber-700 bg-amber-50/50">Bù Hao (Tờ)</th>
              <th className="py-2.5 px-3 text-right w-24 text-slate-800 bg-slate-100/50">In Chạy (Tờ)</th>
              <th className="py-2.5 px-3 text-right w-28 text-emerald-700 bg-emerald-50/50">Giấy Dư Thu Hồi<br /><span className="text-[10px] font-normal">(Tờ)</span></th>
              <th className="py-2.5 px-3 text-center w-36">Trạng Thái Phôi</th>
              <th className="py-2.5 px-3 text-center w-52">Thao Tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={12} className="py-12 text-center text-slate-400">
                  Đang tải dữ liệu cấp phát giấy...
                </td>
              </tr>
            ) : !filteredItems || filteredItems.length === 0 ? (
              <tr>
                <td colSpan={12} className="py-12 text-center text-slate-400">
                  Không tìm thấy dữ liệu cấp phát giấy phù hợp
                </td>
              </tr>
            ) : (
              filteredItems.map((item: any, idx: number) => {
                const stock = Math.round(Number(item.stockPcs) || 0);
                const hasStock = stock > 0;

                return (
                  <DataTableRow
                    key={rowKeyOf(item, idx)}
                    id={rowKeyOf(item, idx)}
                    rowSelection={rowSelection}
                    onClick={() => zIndex.openView('productionOrder', item.productionOrderId || item.lsx)}
                    className="hover:bg-slate-50/80 transition-colors"
                  >
                    <DataTableRowCheckbox id={rowKeyOf(item, idx)} rowSelection={rowSelection} />
                    <td className="py-2.5 px-3 font-mono font-medium text-slate-900">
                      {item.lsx}
                    </td>
                    <td className="py-2.5 px-3 font-medium text-slate-700">
                      {item.customerGroup}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-slate-900">
                        {item.productName || item.itemCode}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono line-clamp-1">
                        {item.poNumber || '---'}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-600">
                      {item.layoutUps || 1}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                      {formatNumber(Math.round(Number(item.requiredSheets) || 0))}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-blue-700 bg-blue-50/20">
                      {formatNumber(Math.round(Number(item.issuedSheets) || 0))}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-amber-700 bg-amber-50/20">
                      {formatNumber(Math.round(Number(item.bufferSheets) || 0))}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-800 bg-slate-100/30">
                      {formatNumber(Math.round(Number(item.printedSheets) || 0))}
                    </td>
                    <td className="py-2.5 px-3 text-right bg-emerald-50/20">
                      <div className={`font-mono font-bold ${hasStock ? 'text-emerald-700' : 'text-slate-400'}`}>
                        {formatNumber(stock)}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {item.isSlitted ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-semibold">
                            ✓ Đã Xả Phôi
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px] font-semibold">
                            ⏳ Chờ Xả Phôi
                          </Badge>
                        )}
                        {item.palletNo && (
                          <span className="text-[10px] text-slate-500 font-mono font-semibold">
                            Pallet: {item.palletNo}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {!item.isSlitted && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleOpenSlitModal(item)}
                            className="h-6 text-[11px] px-2 gap-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                            title="Xác nhận xả phôi mộc (Phase 40)"
                          >
                            <SlidersHorizontal className="h-3 w-3" />
                            Xả Phôi
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenPalletPrintModal(item)}
                          className="h-6 text-[11px] px-2 gap-1 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold"
                          title="In Thẻ Pallet Nhận Diện Giấy In Offset"
                        >
                          <Printer className="h-3 w-3 text-slate-600" />
                          In Thẻ
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedBufferItem(item);
                            setBufferSheetsToAdd(100);
                            setBufferReason('Hao hụt căn chỉnh máy in');
                            setIsBufferModalOpen(true);
                          }}
                          className="h-6 text-[11px] px-2 gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-semibold"
                          title="Cấp bù giấy hao hụt phát sinh khi đang chạy lệnh"
                        >
                          <Plus className="h-3 w-3 mr-0.5" /> Cấp Bù
                        </Button>

                        {hasStock && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenReturnModal(item)}
                            className="h-6 text-[11px] px-2 gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-semibold"
                            title="Thu hồi giấy dôi dư về kho NVL"
                          >
                            <RotateCcw className="h-3 w-3 mr-0.5" /> Thu Hồi
                          </Button>
                        )}
                      </div>
                    </td>
                  </DataTableRow>
                );
              })
            )}
          </tbody>
        </table>
      </ListPageShell>

      {/* Return Surplus Paper / Spot Ink Remnant Modal */}
      <Dialog open={isReturnModalOpen} onOpenChange={setIsReturnModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reclaim-surplus-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-gray-900">
              {reclaimCategory === 'PANTONE_INK'
                ? 'Thu Hồi Mực Pha Pantone Dư Về Kho NVL'
                : 'Thu Hồi Giấy Dư Về Kho NVL'}
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4 py-2">
              {/* Toggle Category: Giấy vs Mực Pha Pantone */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="reclaim-category-paper"
                  onClick={() => {
                    setReclaimCategory('PAPER');
                    setReturnSheets(Math.max(1, Math.round(Number(selectedItem.stockPcs) || 50)));
                    setReturnNotes(`Thu hồi giấy dư của LSX ${selectedItem.lsx}`);
                  }}
                  className={`py-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
                    reclaimCategory === 'PAPER'
                      ? 'bg-emerald-50 border-emerald-500 text-emerald-900 ring-1 ring-emerald-500'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  📄 Giấy In Dôi Dư (Tờ)
                </button>
                <button
                  type="button"
                  data-testid="reclaim-category-pantone"
                  onClick={() => {
                    setReclaimCategory('PANTONE_INK');
                    setReturnSheets(2.5);
                    setReturnNotes(`Thu hồi mực pha dư ${pantoneCode} của LSX ${selectedItem.lsx}`);
                  }}
                  className={`py-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
                    reclaimCategory === 'PANTONE_INK'
                      ? 'bg-purple-50 border-purple-500 text-purple-900 ring-1 ring-purple-500'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  🎨 Mực Pha Pantone Dư (Kg)
                </button>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-1 text-xs">
                <div><span className="text-slate-500">Mã Lệnh SX:</span> <span className="font-mono font-bold text-slate-800">{selectedItem.lsx}</span></div>
                <div><span className="text-slate-500">Mã hàng:</span> <span className="font-semibold text-slate-800">{selectedItem.itemCode}</span></div>
                <div><span className="text-slate-500">Khách hàng:</span> <span className="font-medium text-slate-700">{selectedItem.customerGroup}</span></div>
                {reclaimCategory === 'PAPER' && (
                  <div><span className="text-slate-500">Số lượng giấy dư ghi nhận:</span> <span className="font-mono font-bold text-emerald-600">{formatNumber(Math.round(Number(selectedItem.stockPcs) || 0))} tờ</span></div>
                )}
              </div>

              {reclaimCategory === 'PANTONE_INK' && (
                <div className="space-y-1.5">
                  <Label htmlFor="pantoneCode" className="text-xs font-semibold text-purple-900">
                    Mã màu mực pha đặc chủng (Pantone Code):
                  </Label>
                  <Input
                    id="pantoneCode"
                    data-testid="reclaim-pantone-code-input"
                    value={pantoneCode}
                    onChange={(e) => setPantoneCode(e.target.value)}
                    placeholder="VD: PANTONE 485C, PANTONE 286C..."
                    className="h-8 text-xs font-mono font-bold border-purple-300"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="returnSheets" className="text-xs font-semibold text-slate-700">
                  {reclaimCategory === 'PANTONE_INK'
                    ? 'Khối lượng mực pha thu hồi về Thư viện Mực Dư (Kg):'
                    : 'Số lượng tờ giấy thu hồi về kho:'}
                </Label>
                <Input
                  id="returnSheets"
                  data-testid="reclaim-quantity-input"
                  type="number"
                  step={reclaimCategory === 'PANTONE_INK' ? '0.1' : '1'}
                  value={returnSheets}
                  onChange={(e) => setReturnSheets(Number(e.target.value))}
                  min={0.1}
                  className="h-8 text-xs font-mono font-bold"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="returnNotes" className="text-xs font-semibold text-slate-700">
                  Ghi chú thu hồi:
                </Label>
                <Input
                  id="returnNotes"
                  data-testid="reclaim-notes-input"
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  placeholder="Lý do thu hồi, vị trí kệ lưu trữ..."
                  className="h-8 text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReturnModalOpen(false)}
              className="h-8 text-xs"
            >
              Hủy
            </Button>
            <Button
              size="sm"
              data-testid="btn-confirm-reclaim-surplus"
              onClick={handleConfirmReturn}
              disabled={returnMutation.isPending}
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {returnMutation.isPending ? 'Đang xử lý...' : 'Xác Nhận Thu Hồi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Cấp Bù Giấy Hao Hụt Phát Sinh (In-Flight Buffer) */}
      <Dialog open={isBufferModalOpen} onOpenChange={setIsBufferModalOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Plus className="h-5 w-5 text-indigo-600" />
              Cấp Bù Giấy Hao Hụt Phát Sinh
            </DialogTitle>
          </DialogHeader>

          {selectedBufferItem && (
            <div className="space-y-4 py-2 text-xs">
              <div className="p-3 bg-indigo-50/60 border border-indigo-200 rounded-lg space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Lệnh sản xuất:</span>
                  <span className="font-mono font-bold text-indigo-900">{selectedBufferItem.poOrderId || selectedBufferItem.lsx}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Sản phẩm:</span>
                  <span className="font-semibold text-slate-800 line-clamp-1">{selectedBufferItem.productName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Quy cách giấy:</span>
                  <span className="font-mono font-bold text-slate-700">{selectedBufferItem.notes || selectedBufferItem.itemCode}</span>
                </div>
                <div className="flex justify-between border-t border-indigo-200/60 pt-1 mt-1 text-[11px]">
                  <span className="text-slate-500">Đã cấp hiện tại:</span>
                  <span className="font-mono font-bold text-slate-700">{formatNumber(selectedBufferItem.issuedSheets || 0)} tờ (Bù hao: {formatNumber(selectedBufferItem.bufferSheets || 0)})</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bufferSheetsToAdd" className="text-xs font-semibold text-slate-700">
                  Số lượng tờ giấy cấp bù thêm (*):
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="bufferSheetsToAdd"
                    type="number"
                    min={1}
                    value={bufferSheetsToAdd}
                    onChange={(e) => setBufferSheetsToAdd(Math.max(1, Number(e.target.value)))}
                    className="h-9 text-sm font-mono font-bold text-indigo-900 border-indigo-300"
                  />
                  <div className="flex gap-1">
                    {[50, 100, 200, 500].map((num) => (
                      <Button
                        key={num}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setBufferSheetsToAdd(num)}
                        className="h-9 text-xs px-2 text-indigo-700 hover:bg-indigo-50 border-indigo-200 font-semibold"
                      >
                        +{num}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bufferReason" className="text-xs font-semibold text-slate-700">
                  Lý do cấp bù:
                </Label>
                <Input
                  id="bufferReason"
                  value={bufferReason}
                  onChange={(e) => setBufferReason(e.target.value)}
                  placeholder="VD: Hao hụt căn chỉnh màu in, rách giấy, ..."
                  className="h-8 text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBufferModalOpen(false)}
              className="h-8 text-xs"
            >
              Hủy
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (!selectedBufferItem?.id) return;
                issueBufferMutation.mutate({
                  materialId: selectedBufferItem.id,
                  additionalSheets: bufferSheetsToAdd,
                  reason: bufferReason,
                });
              }}
              disabled={issueBufferMutation.isPending || bufferSheetsToAdd <= 0}
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
            >
              {issueBufferMutation.isPending ? 'Đang cấp bù...' : 'Xác Nhận Cấp Bù Giấy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 40 Paper Slit Modal */}
      {selectedSlitItem && (
        <PaperSlitConfirmModal
          open={isSlitModalOpen}
          onOpenChange={setIsSlitModalOpen}
          item={selectedSlitItem}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [['productionPlanning', 'getPaperAllocationAndSurplus']] });
            refetch();
          }}
        />
      )}

      {/* Phase 40 Pallet Job Tag Print Modal */}
      {selectedPrintItem && (
        <PalletJobTagPrintModal
          open={isPalletPrintModalOpen}
          onOpenChange={setIsPalletPrintModalOpen}
          item={selectedPrintItem}
        />
      )}

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            label: 'Xuất Excel',
            icon: <Download className="h-3.5 w-3.5" />,
            onClick: () => exportRows(filteredItems.filter((it: any, idx: number) => selectedIds.has(rowKeyOf(it, idx)))),
          },
        ]}
      />

      {/* ZIndex Drawer/Modal */}
      <ZIndexView {...zIndex} />
    </>
  );
}

export default PaperAllocationAndSurplusHubPage;
